// Pure geometry for the guided tutorial (#2991): resolving an anchor to the
// elements it names, measuring the box around them, and placing the callout
// card. Ported from the design prototype's spotlight/tutorial-overlay logic.

export const PAD = 6;
export const RING_RADIUS = 15.5;
export const CARD_WIDTH = 450;
const MARGIN = 24;
// The card sits just clear of the spotlight — proximity carries the
// association, so there is no connector to draw.
const GAP = 16;
// Room the card needs below an anchor before it flips above it. A fixed
// figure on purpose: measuring the card would make placement depend on its
// content, and every beat's content is a different height.
const MIN_ROOM = 280;

// Anchors that resolve to existing markup instead of a data-tutorial
// attribute (hand-off §12: the referral tables are already class-hooked).
// `single` limits the anchor to the first match — a snooze cell is one cell,
// not the column of them.
const SELECTOR_ANCHORS = {
  "snooze-cell": { selector: "td.col-snooze", single: true },
};

// Range anchors ([from, to]) bracket a run of table columns off the th classes.
const COLUMN_CLASSES = {
  phase: "col-phase-of-care",
  status: "col-status",
  snooze: "col-snooze",
};

/**
 * The elements an anchor points at, in document order. A data-tutorial name
 * can be on more than one element, and the box then covers all of them — for
 * a run of siblings a beat is about as one thing but which cannot share a
 * wrapper (the sticky suggestions header and the cards under it).
 */
export function anchorElements(anchor) {
  if (Array.isArray(anchor)) {
    const th = document.querySelector(`th.${COLUMN_CLASSES[anchor[0]]}`);
    const table = th && th.closest("table");
    return table ? [table] : [];
  }
  const special = SELECTOR_ANCHORS[anchor];
  if (special) {
    const el = document.querySelector(special.selector);
    return el ? [el] : [];
  }
  return Array.from(document.querySelectorAll(`[data-tutorial="${anchor}"]`));
}

function anchorElement(anchor) {
  return anchorElements(anchor)[0] || null;
}

/** The box around every element an anchor names, in viewport coordinates. */
function anchorRect(anchor) {
  const rects = anchorElements(anchor)
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (!rects.length) return null;

  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * Whether the anchor is still animating, itself or anywhere inside it. An
 * element mid-animation has a rect that is still changing, and an easing
 * curve's tail moves so little that consecutive measurements agree while it
 * is still short of where it lands. Infinite animations (a spinner) never
 * finish, so they are not worth waiting for.
 */
export function stillAnimating(anchor) {
  const el = anchorElement(anchor);
  if (!el || typeof el.getAnimations !== "function") return false;
  return el.getAnimations({ subtree: true }).some((a) => {
    if (a.playState !== "running") return false;
    const timing = a.effect && a.effect.getComputedTiming();
    return timing && timing.iterations !== Infinity;
  });
}

/**
 * The box an anchor has to be inside to count as on screen. Not the
 * viewport: an anchor inside a scrolling pane is clipped by the pane, and a
 * rect that runs past the pane's own bottom edge is a rect the focus ring
 * draws over whatever sits below it.
 */
function viewportFor(el) {
  let node = el.parentElement;
  let top = 0;
  let bottom = window.innerHeight;

  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      /auto|scroll|hidden|overlay/.test(overflowY) &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      const r = node.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
      break;
    }
    node = node.parentElement;
  }

  return { top, bottom };
}

/**
 * Bring a beat's anchor into view, but only if it is not already comfortably
 * on screen — scrolling an anchor that was already visible gives the card
 * two destinations for one move.
 */
export function scrollAnchorIntoView(anchor) {
  const els = anchorElements(anchor);
  const el = els[0];
  if (!el) return;

  // A beat pointing at a whole component shows it from the top, so a pane
  // inside the anchor that a later beat scrolled goes back where it was.
  els.forEach((anchored) =>
    anchored.querySelectorAll("*").forEach((node) => {
      if (node.scrollTop > 0 && node.scrollHeight > node.clientHeight) {
        node.scrollTo({ top: 0, behavior: "smooth" });
      }
    })
  );

  const r = anchorRect(anchor);
  if (!r) return;
  const box = viewportFor(el);
  // Needs its own height plus room for the card, top and bottom.
  const room = 300;
  // An anchor flush against the edge of its pane is not comfortably on
  // screen: the ring lands on whatever the pane ends in.
  const clearance = 24;
  const bottom = r.y + r.height;
  const visible = r.y >= box.top + clearance && bottom <= box.bottom - clearance;
  const roomy = r.y - box.top >= room * 0.4 || box.bottom - bottom >= room * 0.4;
  if (visible && roomy) return;

  // An anchor that nearly fills its pane cannot be centred without losing
  // both its ends; the end that is out of view is the end to scroll to.
  const fills = r.height > (box.bottom - box.top) * 0.7;
  const target = bottom > box.bottom - clearance ? els[els.length - 1] : el;
  target.scrollIntoView({
    behavior: "smooth",
    block: fills ? "nearest" : "center",
  });
}

/**
 * Trims a rect to a square on its smaller side, keeping the top-left corner.
 * For an anchor that is a whole table cell, the cell's width says nothing
 * about the control inside it.
 */
function squared(rect) {
  const side = Math.min(rect.width, rect.height);
  return { x: rect.x, y: rect.y, width: side, height: side };
}

export function measure(anchor, square = false) {
  if (Array.isArray(anchor)) {
    const [from, to] = anchor;
    const a = document.querySelector(`th.${COLUMN_CLASSES[from]}`);
    const b = document.querySelector(`th.${COLUMN_CLASSES[to]}`);
    if (!a || !b) return null;
    const table = a.closest("table");
    if (!table) return null;
    const ar = a.getBoundingClientRect();
    const br = b.getBoundingClientRect();
    const tr = table.getBoundingClientRect();
    return {
      x: ar.left - PAD,
      y: tr.top - PAD,
      width: br.right - ar.left + PAD * 2,
      height: tr.height + PAD * 2,
    };
  }

  const r = anchorRect(anchor);
  if (!r) return null;
  const padded = {
    x: r.x - PAD,
    y: r.y - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
  };
  return square ? squared(padded) : padded;
}

export function sameRect(a, b) {
  if (!a || !b) return false;
  return (
    Math.abs(a.x - b.x) < 1 &&
    Math.abs(a.y - b.y) < 1 &&
    Math.abs(a.width - b.width) < 1 &&
    Math.abs(a.height - b.height) < 1
  );
}

/** A rounded rectangle as a path, so the scrim can punch it out. */
export function roundedRectPath(rect) {
  const r = Math.min(RING_RADIUS, rect.width / 2, rect.height / 2);
  const { x, y, width: w, height: h } = rect;
  return [
    `M${x + r} ${y}`,
    `h${w - 2 * r}`,
    `a${r} ${r} 0 0 1 ${r} ${r}`,
    `v${h - 2 * r}`,
    `a${r} ${r} 0 0 1 ${-r} ${r}`,
    `h${-(w - 2 * r)}`,
    `a${r} ${r} 0 0 1 ${-r} ${-r}`,
    `v${-(h - 2 * r)}`,
    `a${r} ${r} 0 0 1 ${r} ${-r}`,
    "z",
  ].join(" ");
}

/**
 * Where the card goes, derived only from the anchor and the viewport — never
 * from the card's own size. `above` tells the card to hang its own height
 * upwards from `top`, so a taller or shorter beat changes nothing about the
 * position it is moving to.
 */
export function placeCard(rect, place = "center", beside = undefined, pin = undefined) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    if (place === "bottom-left")
      return { left: MARGIN, top: vh - MARGIN, above: true };
    if (place === "top-right")
      return { left: vw - CARD_WIDTH - MARGIN, top: MARGIN, above: false };
    return { left: (vw - CARD_WIDTH) / 2, top: vh / 2, above: false };
  }

  const left = clamp(
    rect.x + rect.width / 2 - CARD_WIDTH / 2,
    MARGIN,
    Math.max(MARGIN, vw - CARD_WIDTH - MARGIN)
  );

  // Top-aligned with the anchor, so the card reads as belonging to that row
  // rather than to the one it happens to be level with.
  const alongside = clamp(rect.y, MARGIN, Math.max(MARGIN, vh - MIN_ROOM - MARGIN));

  // A pinned placement skips the room heuristic: a beat about a modal must
  // not cover it, and its card is short enough to take a tight fit against
  // the anchor's edge over any overlap.
  if (pin === "below")
    return {
      left,
      top: clamp(rect.y + rect.height + GAP, MARGIN, vh - MARGIN),
      above: false,
    };

  if (pin === "above")
    return {
      left,
      top: clamp(rect.y - GAP, MARGIN, vh - MARGIN),
      above: true,
    };

  if (beside === "left" && rect.x > CARD_WIDTH + GAP + MARGIN)
    return { left: rect.x - GAP - CARD_WIDTH, top: alongside, above: false };

  // Forced, unlike beside-left: a beat about a modal must not cover it. Sits
  // a wider gap off the anchor than the stacked placements, and on a viewport
  // too narrow to fit the card fully beside, hugs the right edge with a slim
  // margin — a sliver of overlap beats falling through to a placement over
  // the modal.
  if (beside === "right") {
    const sideGap = 24;
    const edgeMargin = 8;
    return {
      left: Math.min(rect.x + rect.width + sideGap, Math.max(MARGIN, vw - CARD_WIDTH - edgeMargin)),
      top: alongside,
      above: false,
    };
  }

  const roomBelow = vh - (rect.y + rect.height);
  const roomAbove = rect.y;
  const needed = MIN_ROOM + GAP;

  if (roomBelow > needed)
    return { left, top: rect.y + rect.height + GAP, above: false };

  if (roomAbove > needed) return { left, top: rect.y - GAP, above: true };

  // No room above or below — a modal, or an anchor taller than the viewport.
  // Go beside it rather than over it.
  const roomRight = vw - (rect.x + rect.width);
  const roomLeft = rect.x;
  const needsSide = CARD_WIDTH + GAP + MARGIN;

  if (roomRight > needsSide)
    return { left: rect.x + rect.width + GAP, top: alongside, above: false };

  if (roomLeft > needsSide)
    return { left: rect.x - GAP - CARD_WIDTH, top: alongside, above: false };

  // Nowhere beside it either. Hang from just inside its bottom edge, so the
  // card covers the foot of the anchor and leaves its heading visible above.
  return {
    left,
    top: clamp(rect.y + rect.height - GAP, MARGIN, vh - MARGIN),
    above: true,
  };
}

function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

/**
 * Hands the wheel to whatever sits under the pointer. Blocking clicks and
 * passing scroll cannot come from the same property: the blocker has to be
 * hit-testable to swallow a click, which is what puts the wheel on it rather
 * than on the pane below — so the pane is found by hand and scrolled by hand.
 */
export function forwardWheel(event, blocker) {
  const pane = scrollableUnder(event.clientX, event.clientY, blocker);
  if (!pane) return;
  pane.scrollBy({ top: event.deltaY, left: event.deltaX });
}

function scrollableUnder(x, y, blocker) {
  for (const hit of document.elementsFromPoint(x, y)) {
    if (hit === blocker) continue;
    let node = hit;
    while (node) {
      const style = window.getComputedStyle(node);
      if (/auto|scroll/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
  }
  return null;
}
